
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Complaint {
  description: string;
  duration: string;
}

interface ComplaintEntryProps {
  complaints: string;
  duration: string;
  onComplaintsChange: (value: string) => void;
  onDurationChange: (value: string) => void;
}

export function ComplaintEntry({ 
  complaints, 
  duration, 
  onComplaintsChange, 
  onDurationChange 
}: ComplaintEntryProps) {
  const [currentComplaint, setCurrentComplaint] = useState<string>(complaints);
  const [currentDuration, setCurrentDuration] = useState<string>(duration);
  const [savedComplaints, setSavedComplaints] = useState<Complaint[]>(() =>
    complaints
      ? complaints
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
          .map((description) => ({ description, duration: duration || "" }))
      : []
  );

  useEffect(() => {
    // Keep draft complaints in the saved list, but keep entry input empty for new additions.
    setCurrentComplaint("");
    setSavedComplaints(
      complaints
        ? complaints
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .map((description) => ({ description, duration: duration || "" }))
        : []
    );
  }, [complaints, duration]);

  useEffect(() => {
    setCurrentDuration(duration || "");
  }, [duration]);

  const handleSaveComplaint = () => {
    if (currentComplaint.trim()) {
      const newComplaint: Complaint = {
        description: currentComplaint.trim(),
        duration: currentDuration.trim()
      };
      
      const updatedComplaints = [...savedComplaints, newComplaint];
      setSavedComplaints(updatedComplaints);
      
      // Update parent component values
      const complaintsText = updatedComplaints
        .map(c => c.description)
        .join(', ');
      onComplaintsChange(complaintsText);
      onDurationChange(currentDuration.trim());
      
      // Clear current inputs
      setCurrentComplaint("");
      setCurrentDuration("");
    }
  };

  const handleRemoveComplaint = (index: number) => {
    const updatedComplaints = savedComplaints.filter((_, i) => i !== index);
    setSavedComplaints(updatedComplaints);
    
    // Update parent component values
    const complaintsText = updatedComplaints
      .map(c => c.description)
      .join(', ');
    onComplaintsChange(complaintsText);
    if (updatedComplaints.length === 0) {
      onDurationChange("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="chiefComplaints">Complaints</Label>
          <Textarea 
            id="chiefComplaints"
            value={currentComplaint}
            onChange={(e) => setCurrentComplaint(e.target.value)}
            placeholder="Enter patient's complaints"
            className="resize-none h-20"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="complaintsDuration">Duration</Label>
          <div className="flex space-x-2">
            <Input 
              id="complaintsDuration"
              value={currentDuration}
              onChange={(e) => {
                const nextDuration = e.target.value;
                setCurrentDuration(nextDuration);
                onDurationChange(nextDuration);
              }}
              placeholder="e.g., 2 days, 1 week, etc."
            />
            <Button 
              type="button" 
              onClick={handleSaveComplaint}
              disabled={!currentComplaint.trim()}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>
      
      {savedComplaints.length > 0 && (
        <div className="border rounded-md p-3 bg-muted/30">
          <h4 className="text-sm font-medium mb-2">Saved Complaints</h4>
          <div className="space-y-2">
            {savedComplaints.map((complaint, index) => (
              <div key={index} className="flex justify-between items-center bg-background p-2 rounded-md">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{complaint.description}</p>
                  {complaint.duration && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Duration: {complaint.duration}</span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleRemoveComplaint(index)}
                  className="h-8 w-8 p-0 flex-shrink-0 ml-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
